/**
 * Environment configuration for the static bundle.
 *
 * Canon docs/ENVIRONMENTS.md: "API base URLs are explicit per environment" and
 * "browser-exposed env values must be safe to publish". Nothing here may become a secret —
 * every value ends up in a public JavaScript file, and this app in particular must never
 * carry seller identity or a session value in its build.
 *
 * The URLs are validated at startup rather than trusted, so a mis-built bundle fails
 * loudly instead of quietly pointing a production seller at staging data.
 */

export const APP_ENVIRONMENTS = ["local", "staging", "production"] as const;
export type AppEnvironment = (typeof APP_ENVIRONMENTS)[number];

export interface AppConfig {
  environment: AppEnvironment;
  /** Absolute origin (+ optional path) of dealbid-core, never with a trailing slash. */
  apiBaseUrl: string;
  /** Absolute origin of the public site, for links out of the seller app. */
  webBaseUrl: string;
}

export interface RawConfig {
  VITE_ENVIRONMENT?: string | undefined;
  VITE_API_BASE_URL?: string | undefined;
  VITE_WEB_BASE_URL?: string | undefined;
}

function isAppEnvironment(value: unknown): value is AppEnvironment {
  return (
    typeof value === "string" && (APP_ENVIRONMENTS as readonly string[]).includes(value)
  );
}

/**
 * Plain http is a local-development convenience only. A deployed bundle that talked to
 * core over http would expose the seller session cookie in transit, so https is required
 * everywhere else (canon docs/SECURITY.md).
 */
function requireUrl(
  name: keyof RawConfig,
  rawUrl: string | undefined,
  environment: AppEnvironment,
): string {
  if (typeof rawUrl !== "string" || rawUrl.trim() === "") {
    throw new Error(`${name} is required and must be an absolute URL.`);
  }

  let url: URL;
  try {
    url = new URL(rawUrl.trim());
  } catch {
    throw new Error(`${name} must be an absolute URL; received ${rawUrl}.`);
  }

  if (url.protocol !== "https:" && url.protocol !== "http:") {
    throw new Error(`${name} must use http or https; received ${url.protocol}.`);
  }

  if (environment !== "local" && url.protocol !== "https:") {
    throw new Error(`${name} must use https in the ${environment} environment.`);
  }

  return `${url.origin}${url.pathname}`.replace(/\/+$/, "");
}

export function resolveConfig(raw: RawConfig): AppConfig {
  const environment = raw.VITE_ENVIRONMENT;
  if (!isAppEnvironment(environment)) {
    throw new Error(
      `VITE_ENVIRONMENT must be one of ${APP_ENVIRONMENTS.join(", ")}; received ${String(environment)}.`,
    );
  }

  return {
    environment,
    apiBaseUrl: requireUrl("VITE_API_BASE_URL", raw.VITE_API_BASE_URL, environment),
    webBaseUrl: requireUrl("VITE_WEB_BASE_URL", raw.VITE_WEB_BASE_URL, environment),
  };
}

let cached: AppConfig | undefined;

/** Memoised so the bundle validates once, at first use. */
export function getConfig(): AppConfig {
  cached ??= resolveConfig(import.meta.env as RawConfig);
  return cached;
}

/** Joins a `/v1` path onto the configured API base without producing a double slash. */
export function apiUrl(path: string): string {
  const base = getConfig().apiBaseUrl;
  return path.startsWith("/") ? `${base}${path}` : `${base}/${path}`;
}

/*
 * Linking out to the public site is `webUrl` in `src/config/web-routes.ts`, not a function
 * here. It takes a route *name* rather than a path (`APP-011`), and the names belong with
 * the table that declares them.
 */
